import {
    Checkbox,
    DefaultButton, Dropdown, IDropdownOption,
    IPanelProps,
    IRenderFunction, MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Separator, Stack, Text,
    TextField,
    Toggle
} from "@fluentui/react";
import { RootState } from "app/store";
import CustomPanel from "components/controls/CustomPanel";
import { fadeIn, panelButtonsStyles, reconcileBenefit, Tier1To4 } from "global/Application";
import { useApplication } from "global/Application/Context";
import { addUpdateBenefit } from "global/Reducer/dataSlice";
import { IPanelEvent } from "model/Extension";
import {
    IBenefit as FAM_IBenefit,
    IOptionSetValue as FAM_IOptionSetValue,
    TBenefitStatusOptionSetValue as FAM_TBenefitStatusOptionSetValue,
    TProductOptionSetValue as FAM_TProductOptionSetValue
} from "model/FlexibleAttributeModel";
import { useEffect, useMemo, useState } from "react";
import { connect, ConnectedProps, useDispatch } from "react-redux";
import TierWizard, { ITierChange } from "views/TierWizard";

interface IData {
    benefit: FAM_IBenefit | null;
}

interface IProps extends TComponentConnectedProps {
    //TODO: unmount component with delay for animation
    isOpen: boolean;

    // cancellation of transaction/partial actions
    onCancel?: () => void;

    // dialog closure request from parent component
    onDismiss: () => void

    // use this prior to completion of primary action for verification/or to perform action on parent
    onConfirm?: (e?: IPanelEvent<IData>) => void;

    // start condition
    data?: IData;
}


const BenefitProperties = (props: IProps) => {

    const dispatch = useDispatch();
    const application = useApplication();

    // current view state/stages
    const [validationMessage, setValidationMessage] = useState<string | null>(null);

    // selected category
    const [benefit, setBenefit] = useState<FAM_IBenefit | null>(null);
    const [tierChange, setTierChange] = useState<ITierChange | null>(null);


    const [cascadeDeactivateFlag, setCascadeDeactivateFlag] = useState<boolean | null>(null);
    const [cascadeDeactivateNonstandard, setCascadeDeactivateNonstandard] = useState<boolean>(false);
    const [cascadeDeactivateAttributeSet, setCascadeDeactivateAttributeSet] = useState<boolean>(false);

    //TODO: remove dependency
    useEffect(() => {
        // any props that we need to build state on component mount?
        // reset on start
        if (props.isOpen) {

            setTierChange(null);
            setValidationMessage(null);

            if (props.data?.benefit)
                setBenefit(structuredClone(props.data?.benefit));

            setCascadeDeactivateFlag(null);
            setCascadeDeactivateNonstandard(false);
            setCascadeDeactivateAttributeSet(false);
        }
    }, [props.isOpen]);

    const statusOptions = useMemo(() => {
        return props.state.data.config.cache.options_benefitStatusReasons;
    }, []);

    const productOptions = useMemo(() => {
        return props.state.data.config.cache.options_products;
    }, []);

    function onRenderAddProperties(): JSX.Element {

        const isTiered = benefit?.bcbs_istierenabled ?? false
        const isGrandFathered = benefit?.bcbs_isgrandfatherenabled ?? false;
        const isFundingTypeEnabled = benefit?.bcbs_isfundingtypeenabled ?? false;
        const isDualFunded = props.state.data.json.dualFunded?.value ?? false;

        if (!benefit)
            return <div>Benefit not available!</div>;

        return (
            <div className={fadeIn}>
                <Stack tokens={{ childrenGap: 10 }}>
                    <TextField
                        label="Benefit Name"
                        value={benefit?.name ?? ""}
                        disabled={benefit === null}
                        onChange={(event, value) => {
                            setBenefit(t => { return { ...t!, name: value ?? '' }; });
                        }}
                    />

                    <div>
                        <TierWizard
                            disabled={!isTiered}
                            benefit={benefit}
                            variant={'change'}
                            defaultValue={{
                                tiers: benefit.tiers,
                                tierSet: props.state.data.config.tiersets.find(t => t.bcbs_config_tiersetid === benefit.tierSetRef?.id)!
                            }}
                            onChange={(value) => {
                                if ((value?.tierChanges?.length ?? 0) > 0)
                                    setTierChange(value);
                            }}
                        />
                        {!isTiered &&
                            <Text styles={{ root: { color: 'gray' } }} variant={"xSmall"}>Tier support has not been enabled for this category.</Text>
                        }
                    </div>


                    <div>
                        <Dropdown
                            label={"Funding Type"}
                            options={props.state.data.config.cache.options_bcbs_fundingtypes ?? []}
                            disabled={!isDualFunded || !isFundingTypeEnabled}
                            selectedKey={benefit?.fundingType?.value}
                            onChange={(event, option?: IDropdownOption<FAM_IOptionSetValue>) => {
                                setBenefit(t => { return { ...t!, fundingType: option?.data ?? null }; });
                            }}
                        />
                        {!isFundingTypeEnabled &&
                            <div>
                                <Text styles={{ root: { color: 'gray' } }} variant={"xSmall"}>Funding Type support has not been enabled for this category.</Text>
                            </div>
                        }
                        {!isDualFunded &&
                            <div>
                                <Text styles={{ root: { color: 'gray' } }} variant={"xSmall"}>Group configuration has not been setup for dual funding.</Text>
                            </div>
                        }
                    </div>

                    <Dropdown
                        label={"Grandfathered"}
                        options={props.state.data.config.cache.options_bcbs_grandfatheredcode ?? []}
                        disabled={!isGrandFathered}
                        selectedKey={benefit?.grandFathered?.value}
                        onChange={(_, option?: IDropdownOption<FAM_IOptionSetValue>) => {
                            setBenefit(t => { return { ...t!, grandFathered: option?.data ?? null }; });
                        }}
                    />

                    {!isGrandFathered &&
                        <Text styles={{ root: { color: 'gray' } }} variant={"xSmall"}>Grandfathered support has not been enabled for this category.</Text>
                    }

                    <Dropdown
                        label={"Status"}
                        options={statusOptions}
                        selectedKey={benefit?.status.value}
                        onChange={(_, option?: IDropdownOption<FAM_TBenefitStatusOptionSetValue>) => {
                            setBenefit(t => { return { ...t!, status: option?.data! }; });
                        }}
                    />

                    <Dropdown
                        label={"Product"}
                        options={productOptions}
                        disabled={!props.state.status.debug.admin}
                        selectedKey={benefit?.product?.value}
                        onChange={(_, option?: IDropdownOption<FAM_TProductOptionSetValue>) => {
                            setBenefit(t => { return { ...t!, product: option?.data! }; });
                        }}
                    />


                    <Toggle
                        label={"Deactivate"}
                        checked={benefit.deactivated ?? false}
                        disabled={!application.ChangeTypePermissions.benefit.modify_deactivate}
                        onChange={(_, checked) => {
                            setBenefit(t => { return { ...t!, deactivated: checked ?? false }; });
                            setCascadeDeactivateFlag(checked ?? false);
                            setCascadeDeactivateNonstandard(checked ?? false);
                            setCascadeDeactivateAttributeSet(checked ?? false);
                        }}
                    />

                    {cascadeDeactivateFlag != null &&
                        <CustomPanel title="Cascade Deactivation Change" >
                            <Stack tokens={{ childrenGap: 10 }} styles={{ root: { padding: '10px' } }}>
                                <Checkbox
                                    label={`${benefit.deactivated ? "Deactivate" : "Activate"} all Nonstandards`}
                                    checked={cascadeDeactivateNonstandard}
                                    disabled={!application.ChangeTypePermissions.benefit.modify_deactivate || benefit.deactivated}
                                    onChange={(_, checked) => {
                                        setCascadeDeactivateNonstandard(checked ?? false);
                                    }}
                                />

                                <Checkbox
                                    label={`${benefit.deactivated ? "Deactivate" : "Activate"} all Attributesets`}
                                    checked={cascadeDeactivateAttributeSet}
                                    disabled={!application.ChangeTypePermissions.benefit.modify_deactivate || benefit.deactivated}
                                    onChange={(_, checked) => {
                                        setCascadeDeactivateAttributeSet(checked ?? false);
                                    }}
                                />
                            </Stack>

                        </CustomPanel>
                    }

                </Stack>
            </div>
        );
    }

    function onRenderAddPropertiesFooter(): JSX.Element | null {
        if (!benefit)
            return <div>Benefit not available!</div>;

        const disable = benefit.bcbs_istierenabled &&
            benefit.bcbs_istierenabled && tierChange && tierChange.tierSet && tierChange.tiers &&
            tierChange.tiers.some(q => Tier1To4.includes(q.name) || q.name === '' || q.name === null);

        return <div className={fadeIn}>
            <PrimaryButton
                text='Continue'
                styles={panelButtonsStyles}
                disabled={!!disable}
                onClick={() => {
                    let preventDefault = false;

                    if (tierChange) {
                        reconcileBenefit(benefit, tierChange, props.state.data.config);
                    }

                    const confirm: IPanelEvent<IData> = {
                        preventDefault: ({ validationMessage }) => {
                            preventDefault = true;
                            setValidationMessage(validationMessage);
                        },
                        value: {
                            benefit: benefit,
                        }
                    };

                    props.onConfirm?.(confirm);

                    if (preventDefault)
                        return;

                    // cascade deactivation to nonstandards and attribute sets
                    if (cascadeDeactivateNonstandard)
                        benefit.nonStandards.forEach(eachNonStandard => {
                            eachNonStandard.deactivated = benefit.deactivated;
                        });

                    if (cascadeDeactivateAttributeSet)
                        benefit.attributeSets.forEach(eachAttributeSet => {
                            eachAttributeSet.deactivated = benefit.deactivated;
                        });


                    dispatch(addUpdateBenefit([benefit]));

                    // if tier is changing we will deactivate old benefit
                    // TODO this is until merge/compare implements tier change
                    // if (tierChange) {
                    //     // benefit.deactivated = true;
                    //     dispatch(addUpdateBenefit([benefit]));
                    // }

                    // setTimeout(() => {
                    //     LinkTo(props.state, {id:benefit.id, type:'benefit'});
                    //     const path = `/records/blueRelationship/${props.state.status.account?.accountid}/groupConfiguration/${props.state.data.snapshot.bcbs_groupconfigurationsnapshotid}/benefit/${benefit.id}`;
                    //     history.push(path);

                    //     history.push(`${props.state.router.location.pathname.replace(benefit.id, benefit.id)}`);
                    // }, 0);

                    setBenefit(null);

                    props.onDismiss?.();
                }}
            />

            <DefaultButton text='Cancel' onClick={props.onDismiss} styles={panelButtonsStyles} />
        </div>;
    }

    const onRenderUnknown: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        return (<div>Unknown Status</div>)
    }

    const onRenderBody: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        let onRender = onRenderUnknown;

        onRender = onRenderAddProperties;

        return <div>
            <Separator />
            {validationMessage &&
                <MessageBar className={fadeIn} messageBarType={MessageBarType.error} onDismiss={() => setValidationMessage("")}                >
                    {validationMessage}
                </MessageBar>
            }

            {onRender()}
        </div>;
    }

    const onRenderFooterContent: IRenderFunction<IPanelProps> = (contextProps, defaultRender) => {
        let onRender = onRenderUnknown;
        onRender = onRenderAddPropertiesFooter;

        return onRender();
    }

    return (
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            type={PanelType.medium}
            closeButtonAriaLabel="Close"
            headerText="Benefit Properties"
            onRenderFooterContent={onRenderFooterContent}
            isFooterAtBottom={true}
        >
            {onRenderBody()}
        </Panel>
    )
}


function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(BenefitProperties);
